import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeIn, MaskText, SectionTag } from "@/components/Reveal";

const MANIFESTO_BG =
  "https://images.unsplash.com/photo-1579847188804-ecba0e2ea330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwaW5kdXN0cmlhbCUyMGNvbnN0cnVjdGlvbiUyMHNpdGV8ZW58MHx8fHwxNzg4NjQxNTI1fDA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  {
    num: "01",
    title: "Real Problems. Real Tools.",
    body: "We don't start in a boardroom. We start on the jobsite, where the problems are. Every tool we design exists because the work demanded it.",
  },
  {
    num: "02",
    title: "A New American Tool Company.",
    body: "Proxima Centauri Tools is built on real construction jobsite experience — and built to stand shoulder to shoulder with the trades.",
  },
  {
    num: "03",
    title: "This Is Only The First Tool.",
    body: "PrePipe Station is our first flagship. Proxima Centauri Tools is building a lineup of innovative tools — one real problem at a time.",
  },
];

const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative overflow-hidden py-28 md:py-40">
      <div className="absolute inset-0" aria-hidden="true">
        <motion.img
          src={MANIFESTO_BG}
          alt=""
          style={{ y: bgY }}
          className="h-[124%] w-full object-cover opacity-[0.14]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-coal via-coal/70 to-coal" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionTag index="02" label="About Proxima" />

        <h2 data-testid="about-heading" className="mt-8 font-display font-semibold uppercase leading-[0.9] tracking-tight">
          <MaskText
            lines={[
              <span key="1" className="text-[clamp(3rem,7vw,7rem)] text-white">Born On</span>,
              <span key="2" className="text-[clamp(3rem,7vw,7rem)] text-ember red-glow-text">The Jobsite.</span>,
            ]}
          />
        </h2>

        <div className="mt-24 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
          {CHAPTERS.map((chapter, i) => (
            <FadeIn key={chapter.num} delay={i * 0.12} className="bg-coal">
              <article
                data-testid={`manifesto-chapter-${chapter.num}`}
                className="group flex h-full flex-col p-10 transition-[background-color] duration-500 hover:bg-carbon md:p-12"
              >
                <span className="font-display text-7xl font-semibold leading-none text-stroke-silver transition-colors duration-500 group-hover:text-ember group-hover:[-webkit-text-stroke:0px]">
                  {chapter.num}
                </span>
                <h3 className="mt-10 font-display text-3xl font-semibold uppercase leading-tight text-white">
                  {chapter.title}
                </h3>
                <p className="mt-5 font-body text-sm font-light leading-relaxed text-ash">{chapter.body}</p>
                <span className="mt-auto block pt-10">
                  <span className="block h-px w-10 bg-ember/60 transition-[width] duration-500 group-hover:w-full" />
                </span>
              </article>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
